import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, ImageIcon } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const UPLOAD_CAP = 8_000_000; // backend hard limit

export function fileUrl(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : `${BACKEND}${u}`;
}

// Downscale + re-encode a large image in the browser so huge phone photos upload fine.
async function compressImage(file, { maxDim = 2000, maxBytes = 6_500_000 } = {}) {
  if (!file.type.startsWith("image/")) return file;
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  w = Math.max(1, Math.round(w * scale));
  h = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  let quality = 0.9;
  let blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  while (blob && blob.size > maxBytes && quality > 0.4) {
    quality -= 0.15;
    blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  }
  if (!blob) return file;
  const base = (file.name || "image").replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
}

export default function ImageUpload({ value, onChange, label, testid, aspect = "aspect-[3/1]", hint, privateFile = false, publicUpload = false, keepOriginal = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      let toUpload = f;
      if (!keepOriginal && f.type.startsWith("image/") && f.size > 1_500_000) {
        try { toUpload = await compressImage(f); } catch { toUpload = f; }
      }
      if (toUpload.size > UPLOAD_CAP) {
        toast.error("This image is too large even after compression — please pick a smaller photo.");
        return;
      }
      const fd = new FormData();
      fd.append("file", toUpload);
      const endpoint = publicUpload ? "/uploads/public-image" : `/upload${privateFile ? "?private=true" : ""}`;
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      if (!keepOriginal) toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{label}</label>}
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      {value ? (
        <div className={`relative mt-2 rounded-xl overflow-hidden glow-border ${aspect}`} style={{ background: "var(--bg)" }}>
          <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange(null)}
            className="absolute top-2 right-2 w-8 h-8 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform">
            <X className="w-4 h-4 text-primary-c" />
          </button>
          <button type="button" data-testid={`${testid}-replace`} onClick={() => ref.current?.click()} disabled={busy}
            className="absolute bottom-2 right-2 glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 text-primary-c" />} Replace
          </button>
        </div>
      ) : (
        <button type="button" data-testid={`${testid}-trigger`} onClick={() => ref.current?.click()} disabled={busy}
          className={`mt-2 w-full ${aspect} rounded-xl glow-border flex flex-col items-center justify-center gap-2 hover:scale-[1.01] transition-transform`}
          style={{ background: "var(--surface)", color: "var(--text-2)" }}>
          {busy ? <Loader2 className="w-6 h-6 animate-spin text-primary-c" /> : <ImageIcon className="w-6 h-6 text-primary-c" />}
          <span className="text-sm font-bold">{busy ? "Uploading…" : "Upload image"}</span>
          {hint && <span className="text-[10px]">{hint}</span>}
        </button>
      )}
    </div>
  );
}
